import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../../../../api';
import Pagination from '../../../../Tools/Pagination';
import { toast, ToastContainer } from 'react-toastify';

const VehicleChecklistHistoryModal = ({ vehicleId, refreshTrigger, onSubmissionSelect }) => {
  const [historyItems, setHistoryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const capitalizeName = (name) => {
    if (!name) return 'Unknown';
    return name
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };

  /* --------------------------------------------------------------
     FETCH DETAILED HISTORY FOR A SUBMISSION
     -------------------------------------------------------------- */
  const fetchChecklistHistory = useCallback(async () => {
    const currentVehicleId = sessionStorage.getItem('currentVehicleId') || vehicleId;
    const currentSubmissionId = sessionStorage.getItem('currentSubmissionId');

    if (!currentVehicleId || !currentSubmissionId) {
      setHistoryItems([]);
      return;
    }

    setIsLoading(true);
    try {
      const authToken = sessionStorage.getItem('auth_token');
      if (!authToken) throw new Error('Auth token missing');

      const response = await axiosInstance.get(
        `/ems/view-vehicle-history/${currentVehicleId}/${currentSubmissionId}`,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const rawItems = response.data?.data ?? [];
      if (!Array.isArray(rawItems)) throw new Error('Invalid data format');

      const formattedItems = rawItems
        .filter((item) => item.checklist_item_id !== null || item.status !== null)
        .map((item) => {
          const emsItem = item.ems_cheklist_item;
          const childItem = item.ems_cheklist_child_item;

          const childItems = childItem
            ? [{
                id: childItem.id,
                name: childItem.name || 'Unknown Subitem',
                criteria: childItem.criteria || '',
                status: item.child_status,
                notes: item.child_notes || '',
              }]
            : [];

          return {
            id: item.id,
            name: emsItem?.name || 'Unknown Item',
            criteria: emsItem?.criteria?.toString() || '',
            status: item.status,
            notes: item.notes || '',
            submittedAt: item.submitted_at,
            user: item.user,
            childItems,
          };
        });

      formattedItems.sort((a, b) => a.id - b.id);
      setHistoryItems(formattedItems);
      setCurrentPage(1);
    } catch (err) {
      console.error('History fetch error:', err);
      toast.error(err.response?.data?.message || 'Failed to load history');
      setHistoryItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [vehicleId]);

  /* --------------------------------------------------------------
     HANDLE VIEW DETAILS – IMMEDIATE LOAD
     -------------------------------------------------------------- */
  const handleViewDetails = useCallback(
    (submissionBatchId) => {
      sessionStorage.setItem('currentSubmissionId', submissionBatchId);
      // Trigger immediate fetch
      fetchChecklistHistory();
      // Optional: notify parent
      onSubmissionSelect?.(submissionBatchId);
    },
    [fetchChecklistHistory, onSubmissionSelect]
  );

  /* --------------------------------------------------------------
     EFFECTS
     -------------------------------------------------------------- */
  // 1. Auto-fetch when modal opens
  useEffect(() => {
    const modalEl = document.getElementById('bs-checklist-history');
    if (!modalEl) return;

    const onShow = () => {
      // Always try to load if IDs exist
      fetchChecklistHistory();
    };

    modalEl.addEventListener('show.bs.modal', onShow);
    return () => modalEl.removeEventListener('show.bs.modal', onShow);
  }, [fetchChecklistHistory]);

  // 2. Refresh when parent triggers
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchChecklistHistory();
    }
  }, [refreshTrigger, fetchChecklistHistory]);

  // 3. Cleanup on close
  useEffect(() => {
    const modalEl = document.getElementById('bs-checklist-history');
    if (!modalEl) return;

    const onHide = () => {
      sessionStorage.removeItem('currentVehicleId');
      sessionStorage.removeItem('currentSubmissionId');
    };

    modalEl.addEventListener('hidden.bs.modal', onHide);
    return () => modalEl.removeEventListener('hidden.bs.modal', onHide);
  }, []);

  /* --------------------------------------------------------------
     UI HELPERS
     -------------------------------------------------------------- */
  const submittedBy =
    historyItems.length > 0 && historyItems[0].user
      ? capitalizeName(`${historyItems[0].user.first_name} ${historyItems[0].user.last_name}`)
      : 'Unknown';

  const handlePageChange = (page) => setCurrentPage(page);

  const totalItems = historyItems.length;
  const paginatedItems = historyItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const renderHistoryItems = (items) =>
    items.map((item, index) => {
      const serialNumber = (currentPage - 1) * itemsPerPage + index + 1;
      return (
        <React.Fragment key={`history-${item.id}`}>
          {/* Parent Row */}
          <tr>
            <td>{serialNumber}</td>
            <td>
              <p className="mb-0 fw-normal">{item.name}</p>
            </td>
            <td>
              <input type="text" className="form-control" value={item.criteria} disabled />
            </td>
            <td>
              <input
                type="checkbox"
                className="form-check-input"
                checked={item.status === 1}
                disabled
              />
            </td>
            <td>
              <textarea className="form-control" value={item.notes} disabled />
            </td>
          </tr>

          {/* Child Rows */}
          {item.childItems.map((child) => (
            <tr key={`child-${child.id}`} className="child-row">
              <td></td>
              <td className="ps-4">
                <p className="mb-0 fw-normal">↳ {child.name}</p>
              </td>
              <td>
                <input type="text" className="form-control" value={child.criteria} disabled />
              </td>
              <td>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={child.status === 1}
                  disabled
                />
              </td>
              <td>
                <textarea className="form-control" value={child.notes} disabled />
              </td>
            </tr>
          ))}
        </React.Fragment>
      );
    });

  /* --------------------------------------------------------------
     RENDER
     -------------------------------------------------------------- */
  return (
    <>
      <ToastContainer position="top-center" autoClose={4000} />

      <div
        id="bs-checklist-history"
        className="modal fade"
        tabIndex="-1"
        aria-labelledby="bs-checklist-history"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-scrollable modal-xl">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header d-flex align-items-center">
              <h5 className="modal-title">Checklist History</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            {/* Body */}
            <div className="modal-body">
              <div className="text-muted pb-3 text-center">
                Submitted by: <strong>{submittedBy}</strong>
              </div>

              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : historyItems.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  {sessionStorage.getItem('currentSubmissionId')
                    ? 'No history found for this submission'
                    : 'Please select a submission to view details'}
                </div>
              ) : (
                <div className="row">
                  <div className="col-md-12">
                    <table className="table text-nowrap customize-table mb-0 align-middle">
                      <thead className="text-dark">
                        <tr>
                          <th>ID</th>
                          <th>Item</th>
                          <th>Criteria</th>
                          <th>Status</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>{renderHistoryItems(paginatedItems)}</tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalItems > itemsPerPage && (
                    <div className="mt-3">
                      <Pagination
                        total={totalItems}
                        limit={itemsPerPage}
                        page={currentPage}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-danger"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VehicleChecklistHistoryModal;